import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchCoordinatorReport } from "./commissionService";
import "../payments/paymentDashboard.css";

const fmtAmt  = (v) => v != null ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const LINK_BADGE  = { REFERRAL_CODE: "pd-badge-blue", MANUAL: "pd-badge-gray" };
const STATUS_BADGE = { NOT_REGISTERED: "pd-badge-red", REGISTERED: "pd-badge-amber", CONVERTED: "pd-badge-green" };
const COMM_BADGE  = { INITIATED: "pd-badge-gray", PENDING: "pd-badge-amber", PAID: "pd-badge-green", CANCELLED: "pd-badge-red" };
const DEAL_BADGE  = { SUBSCRIPTION: "pd-badge-indigo", COURSE: "pd-badge-purple" };

const PurchasesRow = ({ purchases }) => {
  if (!purchases?.length) return (
    <tr><td colSpan={7} style={{ padding: "10px 16px", color: "#94a3b8", fontSize: "0.82rem" }}>No purchases yet.</td></tr>
  );
  return purchases.map((p) => (
    <tr key={p.commissionId} style={{ background: "#f8fafc" }}>
      <td style={{ paddingLeft: 32 }}><span className={`pd-badge ${DEAL_BADGE[p.dealType] || "pd-badge-gray"}`}>{p.dealType}</span></td>
      <td className="pd-cell-bold">{p.dealName}</td>
      <td className="pd-cell-amount">{fmtAmt(p.amount)}</td>
      <td className="pd-cell-sub">{p.commissionPercent}%</td>
      <td className="pd-cell-amount">{fmtAmt(p.commissionAmount)}</td>
      <td><span className={`pd-badge ${COMM_BADGE[p.commissionStatus] || "pd-badge-gray"}`}>{p.commissionStatus}</span></td>
      <td className="pd-cell-sub">{fmtDate(p.purchasedAt)}</td>
    </tr>
  ));
};

const CoordinatorReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [expanded,  setExpanded]  = useState({});

  useEffect(() => {
    fetchCoordinatorReport(id)
      .then((r) => setData(r.data))
      .catch(() => setError("Failed to load report."))
      .finally(() => setLoading(false));
  }, [id]);

  const toggle = (email) => setExpanded((prev) => ({ ...prev, [email]: !prev[email] }));

  if (loading) return <div className="pd-page"><div className="pd-loading">Loading report…</div></div>;
  if (error)   return <div className="pd-page"><div className="pd-error-bar">{error}</div></div>;
  if (!data)   return null;

  const topCards = [
    { label: "Total Referred",        value: data.totalStudentsReferred },
    { label: "Registered",            value: data.totalRegistered },
    { label: "Converted (Purchased)", value: data.totalConverted },
    { label: "Pending (No Purchase)", value: data.totalPending },
    { label: "Not Registered",        value: data.totalNotRegistered },
    { label: "Commission Earned",     value: fmtAmt(data.totalCommissionEarned) },
  ];

  const commCards = [
    { label: "Total Earned",   value: fmtAmt(data.totalCommissionEarned),  cls: "pd-stat-card" },
    { label: "Total Paid",     value: fmtAmt(data.totalCommissionPaid),    cls: "pd-stat-card pd-stat-success" },
    { label: "Pending Payout", value: fmtAmt(data.totalCommissionPending), cls: "pd-stat-card pd-stat-pending" },
  ];

  return (
    <div className="pd-page">
      <div className="pd-header">
        <div>
          <button className="pd-btn pd-btn-ghost pd-btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 6 }}>← Back</button>
          <h2 className="pd-title">Performance Report — {data.coordinatorName}</h2>
          <div className="pd-cell-sub" style={{ marginTop: 2 }}>
            {data.coordinatorEmail} {data.coordinatorPhone && `· ${data.coordinatorPhone}`} · Default Commission: {data.defaultCommissionPercent}%
          </div>
        </div>
      </div>

      {/* Top 6 summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "0.75rem", padding: "0 1.5rem 1rem" }}>
        {topCards.map((c) => (
          <div key={c.label} className="pd-stat-card">
            <div className="pd-stat-label">{c.label}</div>
            <div className="pd-stat-value">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Commission summary row */}
      <div className="pd-summary-strip" style={{ padding: "0 1.5rem 1rem", display: "flex", gap: "0.75rem" }}>
        {commCards.map((c) => (
          <div key={c.label} className={c.cls} style={{ flex: 1 }}>
            <div className="pd-stat-label">{c.label}</div>
            <div className="pd-stat-value">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Students table */}
      <div className="pd-table-area">
        {!data.students?.length ? (
          <div className="pd-empty">No students referred yet.</div>
        ) : (
          <div className="pd-table-wrap">
            <table className="pd-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Phone</th>
                  <th>Link Method</th>
                  <th>Status</th>
                  <th>Linked On</th>
                  <th>Registered On</th>
                  <th>Purchases</th>
                  <th>Total Commission</th>
                </tr>
              </thead>
              <tbody>
                {data.students.map((s) => {
                  const totalComm = (s.purchases || []).reduce((sum, p) => sum + (p.commissionAmount || 0), 0);
                  const isOpen = expanded[s.studentEmail];
                  return [
                    <tr key={s.studentEmail}>
                      <td>
                        <div className="pd-cell-bold">{s.studentName || "—"}</div>
                        <div className="pd-cell-sub">{s.studentEmail}</div>
                      </td>
                      <td className="pd-cell-sub">{s.studentPhone || "—"}</td>
                      <td><span className={`pd-badge ${LINK_BADGE[s.linkMethod] || "pd-badge-gray"}`}>{s.linkMethod}</span></td>
                      <td><span className={`pd-badge ${STATUS_BADGE[s.registrationStatus] || "pd-badge-gray"}`}>{s.registrationStatus}</span></td>
                      <td className="pd-cell-sub">{fmtDate(s.linkedAt)}</td>
                      <td className="pd-cell-sub">{fmtDate(s.registeredAt)}</td>
                      <td>
                        {s.purchases?.length > 0 ? (
                          <button className="pd-btn pd-btn-ghost pd-btn-sm" onClick={() => toggle(s.studentEmail)}>
                            {s.purchases.length} {isOpen ? "▲" : "▼"}
                          </button>
                        ) : <span className="pd-cell-sub">0</span>}
                      </td>
                      <td className="pd-cell-amount">{totalComm > 0 ? fmtAmt(totalComm) : "—"}</td>
                    </tr>,
                    isOpen && (
                      <tr key={`${s.studentEmail}-exp`}>
                        <td colSpan={8} style={{ padding: 0, background: "#f8fafc" }}>
                          <table className="pd-table" style={{ margin: 0 }}>
                            <thead>
                              <tr style={{ background: "#f1f5f9" }}>
                                <th>Deal Type</th>
                                <th>Plan / Course</th>
                                <th>Amount Paid</th>
                                <th>Commission %</th>
                                <th>Commission Amount</th>
                                <th>Status</th>
                                <th>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              <PurchasesRow purchases={s.purchases} />
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoordinatorReportPage;
